import type { ContactAttributes, ResolvedContactIdentity } from '@chatbot/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from '../Toast.tsx';
import { Button, Input } from '../ui/index.ts';
import { FieldAlternatives, SourceChip } from './SourceChip.tsx';

interface Props {
  identity: ResolvedContactIdentity;
  notes: string | null;
  onSave: (body: { attributes: ContactAttributes; notes: string | null }) => Promise<void>;
  onCancel: () => void;
}

/** Campos editables. `documentType` y `externalId` los escribe el sistema. */
const FIELDS = [
  { key: 'displayName', label: 'Nombre', type: 'text', placeholder: 'Nombre de la persona' },
  { key: 'email', label: 'Correo electrónico', type: 'email', placeholder: 'correo@ejemplo.com' },
  { key: 'phone', label: 'Teléfono alternativo', type: 'tel', placeholder: '+57 300 000 0000' },
  { key: 'documentId', label: 'Documento', type: 'text', placeholder: '1234567890' },
] as const;

export function ContactEditForm({ identity, notes, onSave, onCancel }: Props) {
  const [values, setValues] = useState<Record<string, string>>({
    displayName: identity.displayNameSource === 'manual' ? identity.displayName : '',
    email: identity.email.source === 'manual' ? (identity.email.value ?? '') : '',
    phone: identity.phone.source === 'manual' ? (identity.phone.value ?? '') : '',
    documentId: identity.documentId.source === 'manual' ? (identity.documentId.value ?? '') : '',
  });
  const [noteValue, setNoteValue] = useState(notes ?? '');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      // Sólo se mandan los campos con valor. Los vacíos borran el override y
      // dejan que reaparezca el dato del ERP o del canal.
      const attributes: Record<string, string> = {};
      for (const [key, value] of Object.entries(values)) {
        if (value.trim() !== '') attributes[key] = value.trim();
      }
      await onSave({
        attributes: attributes as ContactAttributes,
        notes: noteValue.trim() === '' ? null : noteValue.trim(),
      });
      toast.success('Contacto actualizado');
    } catch (err) {
      toast.error(`No se pudo guardar: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  /** El valor que se ve hoy, venga de donde venga. */
  function currentValue(key: string): { value: string | null; source: string | null } {
    if (key === 'displayName') {
      return { value: identity.displayName, source: identity.displayNameSource };
    }
    const field = identity[key as 'email' | 'phone' | 'documentId'];
    return { value: field.value, source: field.source };
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {FIELDS.map((f) => {
        const current = currentValue(f.key);
        const overridden = current.source === 'manual';
        return (
          <div key={f.key}>
            <Input
              id={`contact-${f.key}`}
              label={f.label}
              type={f.type}
              value={values[f.key] ?? ''}
              placeholder={
                // Cuando el dato viene de otra fuente, se muestra como
                // placeholder: se ve, pero se entiende que no lo escribiste vos.
                !overridden && current.value ? current.value : f.placeholder
              }
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
            />
            <div className="flex items-center gap-2 mt-1">
              {current.value && <SourceChip source={current.source} />}
              {overridden && (
                <button
                  type="button"
                  onClick={() => setValues((v) => ({ ...v, [f.key]: '' }))}
                  className="text-xs text-primary-600 hover:underline dark:text-primary-400"
                >
                  Quitar mi edición
                </button>
              )}
            </div>
            {f.key === 'displayName' && identity.displayNameAlternatives.length > 0 && (
              <FieldAlternatives
                field={{
                  value: identity.displayName,
                  source: null,
                  alternatives: identity.displayNameAlternatives,
                }}
              />
            )}
          </div>
        );
      })}

      <div>
        <label
          htmlFor="contact-notes"
          className="block text-body-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
        >
          Notas internas
        </label>
        <textarea
          id="contact-notes"
          value={noteValue}
          onChange={(e) => setNoteValue(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Contexto útil para quien atienda a esta persona"
          className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-body-sm text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
          Sólo las ve el equipo. La persona nunca las lee.
        </p>
      </div>

      <div className="flex gap-2">
        <Button id="contact-save" type="submit" variant="primary" size="sm" loading={saving}>
          Guardar
        </Button>
        <Button
          id="contact-cancel"
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={saving}
        >
          Cancelar
        </Button>
      </div>
    </form>
  );
}
